/* سرفصل حساب معین — با دابل‌کلیک روی یک «کل» در AccountingCoding باز می‌شود. */
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng  = s => String(s || '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => (Number(n || 0) === 0) ? '•' : Number(n).toLocaleString('fa-IR');

const API = (path, opts = {}) => fetch(`/api/accounting${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

/* سه حالتِ الزام تفصیلی */
const TRI = {
  0: { label: '—',       bg: '#eceff1', color: '#90a4ae', title: 'غیرفعال' },
  1: { label: 'الزامی',  bg: '#1a237e', color: '#fff',    title: 'در همه اسناد الزامی است' },
  2: { label: 'تفکیکی',  bg: '#64b5f6', color: '#0d2137', title: 'فقط در تفکیک اشخاص' },
};

const emptyForm = (year) => ({
  code: '', name: '', account_entity: 0, entity: 0, is_permanent: false,
  jazb_role: 0, jazb_role1: 0, jazb_role2: 0,
  account_code: 0, account_display: '',
  against_code: 0, against_display: '',
  last_year_code: 0, last_year_display: '',
  note_code: '', note_code1: '', note_code2: '',
  flags: {}, year,
});

export default function AccountingSubCoding({ user, onBack, coding }) {
  // دکمه‌های فرم FRMACCOUNTINGSUBCODING — هر دکمه فقط با مجوز خودش دیده می‌شود
  const vbf = useFormButtons('FRMACCOUNTINGSUBCODING', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const year = coding?.year || new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [meta,     setMeta]     = useState({ necessity: [], account_entity: [], entity: [], jazb_roles: [] });
  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [showAll,  setShowAll]  = useState(false);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(emptyForm(year));
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');
  const [lk,       setLk]       = useState({ field: null, rows: [] });

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 2800); };

  useEffect(() => { API('/sub-coding/meta/').then(j => { if (j.ok) setMeta(j); }); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const p = new URLSearchParams({ coding_id: coding?.id || 0, year, all: showAll ? 1 : 0 });
    const j = await API(`/sub-coding/?${p.toString()}`);
    setLoading(false);
    if (j.ok) setData(j.data); else setError(j.msg);
  }, [coding, year, showAll]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const filtered = data.filter(r => !search ||
    (r.name || '').includes(search) || String(r.code).includes(toEng(search)));

  const openNew = () => { if (!vbf.can('BTNNEW')) return;
    const f = emptyForm(year);
    if (coding?.code) f.code = String(coding.code);
    const fl = {}; meta.necessity.forEach(n => { fl[n.col] = 0; });
    f.flags = fl;
    setForm(f); setLk({ field: null, rows: [] }); setError(''); setModal('new');
  };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      code: String(selected.code), name: selected.name,
      account_entity: selected.account_entity, entity: selected.entity,
      is_permanent: !!selected.is_permanent,
      jazb_role: selected.jazb_role, jazb_role1: selected.jazb_role1, jazb_role2: selected.jazb_role2,
      account_code: selected.account_code, account_display: selected.account_code ? String(selected.account_code) : '',
      against_code: selected.against_code, against_display: selected.against_code ? toFa(selected.against_code) : '',
      last_year_code: selected.last_year_code, last_year_display: selected.last_year_code ? toFa(selected.last_year_code) : '',
      note_code: selected.note_code, note_code1: selected.note_code1, note_code2: selected.note_code2,
      flags: { ...selected.flags }, year,
    });
    setLk({ field: null, rows: [] }); setError(''); setModal('edit');
  };

  const cycleFlag = col => setForm(f => ({ ...f, flags: { ...f.flags, [col]: ((f.flags[col] || 0) + 1) % 3 } }));
  const setAllFlags = v => setForm(f => { const fl = {}; meta.necessity.forEach(n => { fl[n.col] = v; }); return { ...f, flags: fl }; });

  const searchAccount = async q => {
    const j = await API(`/credits/account-search/?q=${encodeURIComponent(q || '')}`);
    if (j.ok) setLk({ field: 'account', rows: j.rows.map(r => ({ code: r.code, label: `${r.account_no} — ${r.title}` })) });
  };
  const searchCode = async (q, field) => {
    const j = await API(`/sub-coding/lookup/code/?q=${encodeURIComponent(q || '')}&year=${field === 'last_year' ? (Number(year) - 1) : year}`);
    if (j.ok) setLk({ field, rows: j.rows.map(r => ({ code: r.code, label: `${toFa(r.code)} — ${r.name}` })) });
  };

  const save = async () => {
    if (!String(form.code).trim()) { setError('کد حساب معین الزامی است'); return; }
    if (!form.name.trim())         { setError('عنوان حساب معین الزامی است'); return; }
    setSaving(true); setError('');
    const body = JSON.stringify({ ...form, code: toEng(form.code) });
    const j = modal === 'new'
      ? await API('/sub-coding/create/', { method: 'POST', body })
      : await API(`/sub-coding/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/sub-coding/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal('delete_blocked'); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={() => selected && setModal('delete')} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
      </div>
      <label style={s.chk}><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} style={{ marginLeft: 6 }} />همه حساب‌ها</label>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجو در عنوان یا کد..." style={{ ...ui.searchInput, flex: 1, minWidth: 200 }} />
      {loading && <span>⏳</span>}
      {filtered.length > 0 && <div style={ui.countBadge}>{toFa(filtered.length)} حساب معین</div>}
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="سرفصل حساب معین"
        subtitle={coding ? `کل ${toFa(coding.code)} — ${coding.name}` : 'سامانه ارجان'}
        toolbar={toolbar} error={!modal ? error : ''}>
        <table style={ui.table}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
            <tr>
              {['کد', 'عنوان', 'ماهیت حساب', 'دائم/موقت', 'تفصیلی فعال', 'بدهکار', 'بستانکار', 'مانده بدهکار', 'مانده بستانکار']
                .map((h, i) => <th key={i} style={ui.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} style={ui.empty}>{loading ? 'در حال بارگذاری...' : 'حساب معینی برای این کل نیست'}</td></tr>}
            {filtered.map((r, i) => {
              const sel = selected?.id === r.id;
              return (
                <tr key={r.id} onClick={() => setSelected(sel ? null : r)} onDoubleClick={openEdit}
                  style={{ background: sel ? '#1a2f50' : i % 2 ? '#fdf6e3' : '#fff', cursor: 'pointer', color: sel ? '#fff' : '#1a1a2e' }}>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontWeight: 700, color: sel ? '#C8A84B' : '#1a237e' }}>{toFa(r.code)}</td>
                  <td style={{ ...ui.td, textAlign: 'right' }} title={r.name}>{r.name}</td>
                  <td style={ui.td}>{r.account_entity_name}</td>
                  <td style={ui.td}>{r.is_permanent ? 'دائم' : 'موقت'}</td>
                  <td style={ui.td}>{r.active_count > 0 ? toFa(r.active_count) : '•'}</td>
                  <td style={{ ...ui.td, ...numCell }}>{numFmt(r.debit_current ?? r.now_debit)}</td>
                  <td style={{ ...ui.td, ...numCell }}>{numFmt(r.now_credit)}</td>
                  <td style={{ ...ui.td, ...numCell, color: sel ? '#81c784' : '#1b5e20' }}>{numFmt(r.remain_debit)}</td>
                  <td style={{ ...ui.td, ...numCell, color: sel ? '#ffb74d' : '#b71c1c' }}>{numFmt(r.remain_credit)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ حساب معین جدید' : `✎ ویرایش: ${selected?.name}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={s.row}>
                <Field label="کد حساب معین *" flex="0 0 22%"><input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} style={s.inp} dir="ltr" /></Field>
                <Field label="عنوان حساب معین *" flex="1"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={s.inp} /></Field>
              </div>
              <div style={s.row}>
                <Field label="ماهیت حساب" flex="1">
                  <select value={form.account_entity} onChange={e => setForm(f => ({ ...f, account_entity: Number(e.target.value) }))} style={s.inp}>
                    {meta.account_entity.map((o, i) => <option key={i} value={i}>{o || '—'}</option>)}
                  </select>
                </Field>
                <Field label="ماهیت (مانده)" flex="1">
                  <select value={form.entity} onChange={e => setForm(f => ({ ...f, entity: Number(e.target.value) }))} style={s.inp}>
                    {meta.entity.map((o, i) => <option key={i} value={i}>{o || '—'}</option>)}
                  </select>
                </Field>
                <Field label="نوع حساب" flex="0 0 20%">
                  <label style={{ ...s.chk, marginTop: 6 }}><input type="checkbox" checked={form.is_permanent} onChange={e => setForm(f => ({ ...f, is_permanent: e.target.checked }))} style={{ marginLeft: 6 }} />دائمی</label>
                </Field>
              </div>
              <div style={s.row}>
                <LookupField label="کد حساب (معین بانک/تفصیلی)" display={form.account_display} flex="1"
                  onType={v => { setForm(f => ({ ...f, account_display: v, account_code: 0 })); searchAccount(v); }}
                  onFocus={() => searchAccount(form.account_display)}
                  rows={lk.field === 'account' ? lk.rows : []}
                  onPick={r => { setForm(f => ({ ...f, account_code: r.code, account_display: r.label })); setLk({ field: null, rows: [] }); }} />
                <LookupField label="کد مقابل" display={form.against_display} flex="1"
                  onType={v => { setForm(f => ({ ...f, against_display: v, against_code: 0 })); searchCode(toEng(v), 'against'); }}
                  onFocus={() => searchCode(toEng(form.against_display), 'against')}
                  rows={lk.field === 'against' ? lk.rows : []}
                  onPick={r => { setForm(f => ({ ...f, against_code: r.code, against_display: r.label })); setLk({ field: null, rows: [] }); }} />
                <LookupField label="کد سال قبل" display={form.last_year_display} flex="1"
                  onType={v => { setForm(f => ({ ...f, last_year_display: v, last_year_code: 0 })); searchCode(toEng(v), 'last_year'); }}
                  onFocus={() => searchCode(toEng(form.last_year_display), 'last_year')}
                  rows={lk.field === 'last_year' ? lk.rows : []}
                  onPick={r => { setForm(f => ({ ...f, last_year_code: r.code, last_year_display: r.label })); setLk({ field: null, rows: [] }); }} />
              </div>
              <div style={s.row}>
                <Field label="نقش جذب ۱" flex="1"><SelJazb v={form.jazb_role}  set={v => setForm(f => ({ ...f, jazb_role: v }))}  opts={meta.jazb_roles} /></Field>
                <Field label="نقش جذب ۲" flex="1"><SelJazb v={form.jazb_role1} set={v => setForm(f => ({ ...f, jazb_role1: v }))} opts={meta.jazb_roles} /></Field>
                <Field label="نقش جذب ۳" flex="1"><SelJazb v={form.jazb_role2} set={v => setForm(f => ({ ...f, jazb_role2: v }))} opts={meta.jazb_roles} /></Field>
              </div>
              <div style={s.row}>
                <Field label="یادداشت" flex="1"><input value={form.note_code}  onChange={e => setForm(f => ({ ...f, note_code: e.target.value }))}  style={s.inp} dir="ltr" /></Field>
                <Field label="یادداشت ۱" flex="1"><input value={form.note_code1} onChange={e => setForm(f => ({ ...f, note_code1: e.target.value }))} style={s.inp} dir="ltr" /></Field>
                <Field label="یادداشت ۲" flex="1"><input value={form.note_code2} onChange={e => setForm(f => ({ ...f, note_code2: e.target.value }))} style={s.inp} dir="ltr" /></Field>
              </div>
              <div style={s.necBox}>
                <div style={s.necHead}>
                  <span style={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>🔖 الزام تفصیلی‌ها</span>
                  <span style={s.legend}>
                    <span style={{ ...s.legDot, background: TRI[1].bg }} /> الزامی
                    <span style={{ ...s.legDot, background: TRI[2].bg, marginRight: 10 }} /> تفکیکی
                    <span style={{ ...s.legDot, background: TRI[0].bg, marginRight: 10 }} /> غیرفعال
                  </span>
                  <span style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => setAllFlags(0)} style={s.miniBtn}>پاک‌کردن همه</button>
                  </span>
                </div>
                <div style={s.necGrid}>
                  {meta.necessity.map(n => {
                    const st = form.flags[n.col] || 0;
                    const t = TRI[st];
                    return (
                      <button type="button" key={n.col} onClick={() => cycleFlag(n.col)} title={t.title}
                        style={{ ...s.necChip, background: t.bg, color: t.color, borderColor: st ? t.bg : '#e0e0e0' }}>
                        <span style={{ flex: 1, textAlign: 'right' }}>{n.label}</span>
                        <span style={s.necState}>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ در حال ذخیره...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: 28 }}>
              <div style={{ fontSize: 34, marginBottom: 10 }}>🗑️</div>
              <div style={{ fontSize: 14, color: '#555' }}>حساب معین زیر حذف خواهد شد:</div>
              <div style={{ background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontWeight: 700, color: '#b71c1c' }}>
                [{toFa(selected?.code)}] {selected?.name}
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'حذف شود'}</button>}
            </div>
          </div>
        </div>
      )}
      {modal === 'delete_blocked' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#f57c00)' }}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: 28, fontSize: 14, color: '#1a2f50', fontWeight: 700, lineHeight: 1.8 }}>{error || 'این رکورد در جام ارجان مورد استفاده قرار گرفته و قابل حذف نیست'}</div>
            <div style={ui.mFooter}><button onClick={() => { setModal(null); setError(''); }} style={ui.btnSave}>باشه</button></div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}

const Field = ({ label, flex = 1, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>{label}</label>
    {children}
  </div>
);
const SelJazb = ({ v, set, opts }) => (
  <select value={v} onChange={e => set(Number(e.target.value))} style={s.inp}>
    {opts.map((o, i) => <option key={i} value={i}>{o || '—'}</option>)}
  </select>
);
const LookupField = ({ label, display, onType, onFocus, rows, onPick, flex = 1 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex, position: 'relative' }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>{label}</label>
    <input value={display} onChange={e => onType(e.target.value)} onFocus={onFocus} placeholder="تایپ کنید..." style={s.inp} />
    {rows.length > 0 && (
      <div style={s.dropdown}>
        {rows.map((r, i) => <div key={i} onClick={() => onPick(r)} style={s.dropItem}>{r.label}</div>)}
      </div>
    )}
  </div>
);

// ستون عددی + استایل‌های مخصوص فرم این صفحه
const numCell = { direction: 'ltr', textAlign: 'left', fontWeight: 600 };
const s = {
  chk: { display: 'flex', alignItems: 'center', fontSize: 12, color: '#444', cursor: 'pointer', background: '#fff', padding: '5px 10px', borderRadius: 6, border: '1px solid #e0e0e0' },
  row: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  inp: { padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: "'Vazirmatn',Tahoma,sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, maxHeight: 190, overflowY: 'auto', zIndex: 60, boxShadow: '0 6px 20px rgba(0,0,0,0.18)' },
  dropItem: { padding: '7px 11px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f5f5f5', textAlign: 'right' },
  necBox: { border: '1px solid #e8eaf6', borderRadius: 10, padding: 14, background: '#f8f9fb', marginTop: 6 },
  necHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  legend: { fontSize: 11, color: '#666', display: 'flex', alignItems: 'center' },
  legDot: { display: 'inline-block', width: 12, height: 12, borderRadius: 3, marginLeft: 4, verticalAlign: 'middle' },
  miniBtn: { padding: '4px 10px', background: '#eceff1', border: '1px solid #cfd8dc', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 },
  necGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(215px,1fr))', gap: 8 },
  necChip: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: '1.5px solid', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, transition: 'all .12s' },
  necState: { fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: 'rgba(255,255,255,0.25)' },
};
